import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@core/services/auth-new.service';

export interface CompanyApplication {
  name: string;
  BaseUrl: string;
  icon?: string;
  imgIcon?: string;
  logo?: string;
  isAvailable?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  getAvailableCompanies(): CompanyApplication[] {
    try {
      const applications = localStorage.getItem('Applications');
      if (!applications) {
        return [];
      }

      const companies = JSON.parse(applications) as CompanyApplication[];
      return this.processCompanyIcons(companies);
    } catch (error) {
      console.error('Error getting available companies:', error);
      return [];
    }
  }

  getCurrentCompany(): string {
    return this.authService.getCurrentCompany();
  }

  getAlternativeCompany(): CompanyApplication | null {
    const companies = this.getAvailableCompanies();
    const currentCompany = this.getCurrentCompany();
    return companies.find(company => company.name !== currentCompany) || null;
  }

  hasMultipleCompanies(): boolean {
    return this.getAvailableCompanies().length > 1;
  }

  async changeCompany(company: CompanyApplication): Promise<void> {
    if (!company?.name) {
      throw new Error('Invalid company provided');
    }

    const companyName = company.name.toLowerCase();
    const switched = await this.authService.switchCompany(companyName);

    if (!switched) {
      throw new Error(`Could not switch to ${companyName}`);
    }

    this.updateCompanyClass(companyName);
  }

  private processCompanyIcons(companies: CompanyApplication[]): CompanyApplication[] {
    const iconMap: Record<string, string> = {
      'unity': '/assets/icons/logo-unity.png',
      'mingle': '/assets/icons/logo-mingle.png',
    };
    return companies.map(company => {
      const key = Object.keys(iconMap).find(k => company.name.toLowerCase().includes(k));
      if (key) {
        company.imgIcon = iconMap[key];
      }
      return company;
    });
  }

  private updateCompanyClass(companyName: string): void {
    try {
      const companyNameClass = companyName?.replace(/\s+/g, '-').toLowerCase();
      const bodyElement = document.body;

      bodyElement.classList.forEach(cls => {
        if (cls.startsWith('company-')) {
          bodyElement.classList.remove(cls);
        }
      });

      if (companyNameClass) {
        bodyElement.classList.add(`company-${companyNameClass}`);
      }
    } catch (error) {
      console.error('Error updating company class:', error);
    }
  }

  getCompanyDisplayInfo(companyName: string): { name: string; icon: string; displayName: string } {
    const defaultInfo = {
      name: companyName,
      icon: '/assets/icons/default-company.png',
      displayName: companyName.charAt(0).toUpperCase() + companyName.slice(1)
    };

    const lower = companyName.toLowerCase();
    if (lower.includes('unity')) {
      return {
        name: companyName,
        icon: '/assets/icons/logo-unity.png',
        displayName: 'Unity Wireless'
      };
    }
    if (lower.includes('mingle')) {
      return {
        name: companyName,
        icon: '/assets/icons/logo-mingle.png',
        displayName: 'Mingle Mobile'
      };
    }
    return defaultInfo;
  }
}

